/**
 * Pure helpers that shape catalog products and the presentation into what the
 * branded line sheet prints (KP-4545). Kept out of the components so they are
 * testable without a DOM.
 */

import { priceBasisLabel } from './priceFormat'

const DATE_FORMAT = { day: 'numeric', month: 'long', year: 'numeric' }

/**
 * The presentation's sections with the products loaded so far, in the
 * section's own order; `total` keeps the full count for the heading.
 *
 * Products arrive in catalog order, page by page, while sections group them,
 * so a later page fills sections above the buyer. With `pending` (more pages
 * to come) every style not loaded yet keeps its place as `{ _id, pending }`,
 * so the page never moves under the buyer. Without it, a style that never
 * arrived is left out, and so is a section with nothing loaded.
 */
export function bucketSections(sections, productsById, { pending = false } = {}) {
  const slot = id => productsById[id] || (pending ? { _id: id, pending: true } : null)
  return (sections || [])
    .map(section => {
      const ids = Array.isArray(section.product_ids) ? section.product_ids : []
      return {
        ...section,
        total: ids.length,
        products: ids.map(slot).filter(Boolean),
      }
    })
    .filter(section => section.products.length)
}

const itemsOf = product => (Array.isArray(product && product.items) ? product.items : [])

const colourOf = item => ({
  name: item.color,
  family: item.colorFamily || '',
  hex: /^#[0-9a-f]{6}$/i.test(item.hexCode || '') ? item.hexCode : null,
})

/** Distinct colours of a product, in catalog order. */
export function productColours(product) {
  const named = itemsOf(product).filter(item => item && item.color)
  const firsts = named.filter((item, i) => named.findIndex(other => other.color === item.color) === i)
  return firsts.map(colourOf)
}

/** The product's https images. */
export function productImages(product) {
  return (Array.isArray(product && product.images) ? product.images : [])
    .filter(url => typeof url === 'string' && url.startsWith('https://'))
}

function smallestMoq(products) {
  const values = (products || [])
    .map(p => Number(p && p.minQtyPerStyle))
    .filter(n => Number.isFinite(n) && n > 0)
  return values.length ? Math.min(...values) : 0
}

function sheetCurrency(products) {
  const codes = new Set((products || []).map(p => p && p.currency).filter(c => /^[A-Z]{3}$/.test(c || '')))
  return codes.size === 1 ? [...codes][0] : ''
}

/** The facts column. Empty facts are left out, never printed as 0 or "TBC". */
export function storyFacts(rawFacts, priceVisibility, products = []) {
  const facts = rawFacts || {}
  const categories = Array.isArray(facts.categories) ? facts.categories : []
  const rows = []
  const push = (label, value) => {
    if (value) rows.push({ label, value: String(value) })
  }
  push('Styles', facts.styles > 0 ? facts.styles : '')
  push('Categories', categories.filter(Boolean).join(', '))
  push('Colourways', facts.colourways > 0 ? facts.colourways : '')
  push('Prices', priceBasisLabel(priceVisibility))
  const moq = smallestMoq(products)
  push('Minimum order', moq ? `From ${moq} per style` : '')
  push('Currency', sheetCurrency(products))
  return rows
}

function domainOf(website) {
  try {
    return new URL(website).hostname.replace(/^www\./, '')
  } catch (e) {
    return ''
  }
}

function longDate(value) {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-GB', { ...DATE_FORMAT, timeZone: 'UTC' })
}

/** The sources sentence (KP-4545 AC): where the page's facts come from. */
export function sourcesLine({ brandName, website, updatedAt }) {
  const date = updatedAt ? longDate(updatedAt) : ''
  const domain = website ? domainOf(website) : ''
  const catalog = `Product details from ${brandName}'s catalog on Kingpin${date ? `, updated ${date}` : ''}.`
  return domain ? `${catalog} Brand story from ${domain}.` : catalog
}

/** True when text holds an em or en dash, which client copy never prints. */
export const hasDash = text => /[–—]/.test(String(text || ''))
